import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import { useLazyAddCancellationQuery, useLazyFetchCancellationsQuery } from '../api/eecs-cancellations.api';
import {
  EecsCancellationsAC,
  setEecsCancellationModal,
  setSelectedEecsCancellation,
} from '../store/eecs-cancellations.slice';
import {
  AddEecsCancellationPayload,
  FetchEecsCancellationsPayload,
  IEecsCancellation,
  IEecsCancellationModal,
} from '../types/eecs-cancellations.types';

interface OpenModal {
  modal: IEecsCancellationModal;
  eecsCancellation: IEecsCancellation | null;
}

interface UseEecsCancellations {
  fetchEecsCancellationsHandler: (payload: FetchEecsCancellationsPayload) => Promise<boolean>;
  addEecsCancellationHandler: (payload: AddEecsCancellationPayload) => Promise<IEecsCancellation | null>;
  openEecsCancellationsModal: (payload: OpenModal) => void;
  clearEecsCancellationsState: () => void;
  closeEecsCancellationsModal: () => void;
}

export const useEecsCancellations = (): UseEecsCancellations => {
  const dispatch = useAppDispatch();

  const [fetchCancellationsQuery] = useLazyFetchCancellationsQuery();
  const [addCancellationQuery] = useLazyAddCancellationQuery();

  const fetchEecsCancellationsHandler = async (payload: FetchEecsCancellationsPayload) => {
    dispatch(EecsCancellationsAC.toggleLoading(true));
    const { data: response, error } = await fetchCancellationsQuery(payload);
    dispatch(EecsCancellationsAC.toggleLoading(false));
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get EECS Cancellations!');
    } else {
      batch(() => {
        dispatch(EecsCancellationsAC.setItems(response.data));
        dispatch(EecsCancellationsAC.setPaginationMeta(response.meta));
      });
    }

    return !isError;
  };

  const addEecsCancellationHandler = async (payload: AddEecsCancellationPayload) => {
    dispatch(EecsCancellationsAC.toggleLoading(true));
    const { data: response, error } = await addCancellationQuery(payload);
    dispatch(EecsCancellationsAC.toggleLoading(false));
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to add EECS Cancellations!');
      return null;
    } else {
      toast.success('Cancellation request successful, cancellation statement will be available for download later.');
      dispatch(EecsCancellationsAC.addItem(response.data));
      return response.data;
    }
  };

  const openEecsCancellationsModal = ({ modal, eecsCancellation }: OpenModal) => {
    batch(() => {
      dispatch(setSelectedEecsCancellation(eecsCancellation));
      dispatch(setEecsCancellationModal(modal));
    });
  };

  const closeEecsCancellationsModal = () => {
    batch(() => {
      dispatch(setSelectedEecsCancellation(null));
      dispatch(setEecsCancellationModal(null));
    });
  };

  const clearEecsCancellationsState = () => {
    batch(() => {
      dispatch(EecsCancellationsAC.clearItems());
      dispatch(EecsCancellationsAC.clearFilters());
      dispatch(EecsCancellationsAC.clearSorts());
      dispatch(EecsCancellationsAC.setCurrentPage(1));
      dispatch(setSelectedEecsCancellation(null));
      dispatch(setEecsCancellationModal(null));
    });
  };

  return {
    fetchEecsCancellationsHandler,
    addEecsCancellationHandler,
    openEecsCancellationsModal,
    closeEecsCancellationsModal,
    clearEecsCancellationsState,
  };
};
